import type { JSX } from 'react';
import type { ServiceBusHealthSummary } from '../../services/api';

interface QueueHealthCardsProps {
  summary: {
    totalQueues: number;
    totalActiveMessages: number;
    totalDeadLetterMessages: number;
    totalScheduledMessages: number;
  };
  healthSummary?: ServiceBusHealthSummary;
  isLoading: boolean;
}

export function QueueHealthCards({ summary, healthSummary, isLoading }: QueueHealthCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="card animate-pulse border border-gray-200 dark:border-gray-700">
            <div className="h-4 w-2/5 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="mt-3 h-8 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard title="Overall Status" value={getStatusLabel(healthSummary?.overallStatus)} icon={StatusIcon} valueClassName={getStatusValueClassName(healthSummary?.overallStatus)} />
      <StatCard title="Total Queues" value={summary.totalQueues} icon={QueueIcon} />
      <StatCard title="Active Messages" value={summary.totalActiveMessages} icon={ActivityIcon} />
      <StatCard
        title="Dead Letter Messages"
        value={summary.totalDeadLetterMessages}
        icon={WarningIcon}
        valueClassName={summary.totalDeadLetterMessages > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}
      />
    </div>
  );
}

function StatCard({
  title,
  value,
  valueClassName,
  icon: Icon,
}: {
  title: string;
  value: number | string;
  valueClassName?: string;
  icon: (props: { className?: string }) => JSX.Element;
}) {
  return (
    <article className="card border border-gray-200 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg dark:border-gray-700">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</p>
          <p className={`mt-2 text-3xl font-semibold text-gray-900 dark:text-gray-100 ${valueClassName ?? ''}`}>{value}</p>
        </div>
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </article>
  );
}

function getStatusLabel(status?: string): string {
  switch (status?.toLowerCase()) {
    case 'healthy':
      return 'Healthy';
    case 'warning':
      return 'Warning';
    case 'critical':
      return 'Critical';
    default:
      return 'Unknown';
  }
}

function getStatusValueClassName(status?: string): string {
  switch (status?.toLowerCase()) {
    case 'healthy':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'warning':
      return 'text-orange-600 dark:text-orange-400';
    case 'critical':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-gray-700 dark:text-gray-300';
  }
}

function StatusIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
    </svg>
  );
}

function QueueIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <rect x="3" y="5" width="18" height="4" rx="1" />
      <rect x="3" y="10" width="18" height="4" rx="1" />
      <rect x="3" y="15" width="18" height="4" rx="1" />
    </svg>
  );
}

function ActivityIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 14h4l2-5 4 9 2-4h4" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l10 18H2L12 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v5" />
      <circle cx="12" cy="17" r="1" fill="currentColor" />
    </svg>
  );
}
